import { set, toggle } from '@shopworx/services/util/store.helper';
import { sortArray } from '@shopworx/services/util/sort.service';
import { getReasonIsPlanned } from './utils/downtime-reason';

const CLOSURE_ASSET_ID = 19;

export default ({
  namespaced: true,
  state: {
    masterData: [],
    drawer: false,
    onboarded: true,
    machines: [],
    shifts: [],
    downtimeList: [],
    loading: false,
    error: false,
    downtimeReasons: [],
    downtimeCount: 0,
    filteredDowntimeCount: null,
    toggleSelection: false,
    selectedDowntimes: [],
    lastRefreshedAt: null,
    assetId: 0,
  },
  mutations: {
    setMasterData: set('masterData'),
    setSelectedDowntimes: set('selectedDowntimes'),
    setToggleSelection: set('toggleSelection'),
    setDrawer: set('drawer'),
    toggleDrawer: toggle('drawer'),
    setOnboarded: set('onboarded'),
    setMachines: set('machines'),
    setShifts: set('shifts'),
    setLoading: set('loading'),
    setError: set('error'),
    setDowntimeReasons: set('downtimeReasons'),
    setDowntimeCount: set('downtimeCount'),
    setFilteredDowntimeCount: set('filteredDowntimeCount'),
    setDowntimeList: set('downtimeList'),
    setLastRefreshedAt: set('lastRefreshedAt'),
    setAssetId: set('assetId'),
  },
  actions: {
    getOnboardingState: async ({ commit, dispatch }) => {
      const isDowntimeReasonElementAvailable = await dispatch('getDowntimeReasonsElement');
      if (isDowntimeReasonElementAvailable) {
        commit('setOnboarded', true);
      } else {
        commit('setOnboarded', false);
      }
    },

    getMasterData: async ({
      commit,
      dispatch,
      rootGetters,
    }) => {
      const licensedAssets = rootGetters['user/licensedAssets'];
      const masterElements = await dispatch(
        'industry/getMasterElements',
        null,
        { root: true },
      );
      const masterAssets = await dispatch(
        'industry/getAssets',
        null,
        { root: true },
      );
      if (masterElements && masterElements.length) {
        const filteredMasterElements = masterElements
          .filter((elem) => elem.masterElement.elementName === 'downtimereasons')
          .map((elem) => {
            if (elem.masterElement.assetBased) {
              if (masterAssets && masterAssets.length) {
                const availableAssets = elem.masterTags.map((tag) => tag.assetId);
                const provisionedAssets = [...new Set(availableAssets)];
                return provisionedAssets
                  .filter((asset) => licensedAssets.includes(asset))
                  .map((provisionedAsset) => {
                    const tags = elem.masterTags.filter((tag) => tag.assetId === provisionedAsset);
                    const { assetName, assetDescription } = masterAssets
                      .find((asset) => asset.id === provisionedAsset);
                    return {
                      tags: tags.filter((t) => !t.hide),
                      hiddenTags: tags.filter((t) => t.hide),
                      success: false,
                      loading: false,
                      assetId: provisionedAsset,
                      element: elem.masterElement,
                      title: `${elem.masterElement.elementDescription} - ${assetDescription}`,
                      expectedFileName: `${elem.masterElement.elementName}-${assetName}.csv`,
                    };
                  });
              }
            }
            return {
              assetId: 0,
              success: false,
              loading: false,
              hiddenTags: elem.masterTags.filter((t) => t.hide),
              tags: elem.masterTags.filter((t) => !t.hide),
              element: elem.masterElement,
              title: elem.masterElement.elementDescription,
              expectedFileName: `${elem.masterElement.elementName}.csv`,
            };
          });
        commit('setMasterData', filteredMasterElements.flat());
        return true;
      }
      return false;
    },

    getDowntimeReasonsElement: async ({ dispatch }) => {
      const downtimeReasonsElement = await dispatch(
        'element/getElement',
        'downtimereasons',
        { root: true },
      );
      return downtimeReasonsElement;
    },

    fetchSingleMachine: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        {
          elementName: 'machine',
          query: '?pagenumber=1&pagesize=1',
        },
        { root: true },
      );
      if (records && records.length) {
        commit('setAssetId', records[0].assetid);
      }
    },

    fetchMachines: async ({ commit, rootState, dispatch }) => {
      const { assetId } = rootState.downtimeLog;
      let records = await dispatch(
        'element/getRecords',
        {
          elementName: 'machine',
        },
        { root: true },
      );
      if (records && records.length) {
        if (assetId === CLOSURE_ASSET_ID) {
          records = records.filter((rec) => rec.isdowntime === true);
        }
        commit('setMachines', records);
        return true;
      }
      return false;
    },

    fetchShifts: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        {
          elementName: 'businesshours',
          query: '?sortquery=sortindex==1',
        },
        { root: true },
      );
      if (records && records.length) {
        const allShifts = records.filter((rec) => rec.type === 'shift');
        const shiftList = [...new Set(allShifts.map((item) => item.name))];
        commit('setShifts', shiftList);
        return true;
      }
      return false;
    },

    fetchDowntimeReasons: async ({ commit, dispatch }) => {
      const records = await dispatch(
        'element/getRecords',
        { elementName: 'downtimereasons' },
        { root: true },
      );
      if (records) {
        const downtimeReasons = records.map((record) => {
          const isPlanned = getReasonIsPlanned(record);

          if (isPlanned === null) {
            return record;
          }

          return {
            ...record,
            isPlanned,
          };
        });

        commit('setDowntimeReasons', downtimeReasons);
        return true;
      }
      return false;
    },

    fetchDowntimeList: async ({
      commit,
      dispatch,
      state,
      rootState,
      rootGetters,
    }) => {
      const filters = rootGetters['webApp/filters'];
      const { assetId } = rootState.downtimeLog;
      const date = parseInt(filters.date.value.replace(/-/g, ''), 10);
      const machineField = assetId === CLOSURE_ASSET_ID ? 'linename' : 'machinename';
      commit('setLoading', true);
      commit('setDowntimeList', []);
      commit('setError', false);
      const data = await dispatch(
        'element/getRecordsWithCount',
        {
          elementName: 'downtime',
          query: `?query=date==${date}`,
        },
        { root: true },
      );
      if (data && data.results) {
        if (assetId === CLOSURE_ASSET_ID || assetId) {
          let { machines } = state;
          if (assetId === CLOSURE_ASSET_ID) {
            machines = [...new Set(machines
              .map((mac) => mac[machineField] || mac.machinename)
              .filter(Boolean))];
          } else {
            machines = machines.map((mac) => mac[machineField] || mac.machinename);
          }
          data.results = data.results.filter((rec) => (
            machines.includes(rec[machineField] || rec.machinename)
          ));
        }
        commit('setDowntimeList', data.results);
        commit('setDowntimeCount', data.results.length);
        commit('setError', false);
        commit('setLastRefreshedAt', new Date().toLocaleTimeString('en-GB'));
      } else {
        commit('setDowntimeList', []);
        commit('setDowntimeCount', 0);
        commit('setError', true);
      }
      commit('setLoading', false);
    },

    updateReason: async ({ dispatch, commit, state }, { id, payload }) => {
      const updated = await dispatch(
        'element/updateRecordById',
        {
          elementName: 'downtime',
          id,
          payload,
        },
        { root: true },
      );
      if (updated) {
        let { downtimeList } = state;
        downtimeList = downtimeList.map((dt) => {
          // eslint-disable-next-line
          if (dt._id === id) {
            return {
              ...dt,
              ...payload,
            };
          }
          return dt;
        });
        commit('setDowntimeList', []);
        commit('setDowntimeList', downtimeList);
        commit('helper/setAlert', {
          show: true,
          type: 'success',
          message: 'DOWNTIME_UPDATE',
        }, {
          root: true,
        });
      } else {
        commit('helper/setAlert', {
          show: true,
          type: 'error',
          message: 'DOWNTIME_UPDATE',
        }, {
          root: true,
        });
      }
    },
  },
  getters: {
    isClosureAsset: ({ assetId }) => assetId === CLOSURE_ASSET_ID,

    machineFilterField: ({ assetId }) => (
      assetId === CLOSURE_ASSET_ID ? 'linename' : 'machinename'
    ),

    machineList: ({ machines, assetId }) => {
      let machineList = [];
      if (machines && machines.length) {
        if (assetId === CLOSURE_ASSET_ID) {
          const lines = [...new Set(machines
            .map(({ linename }) => linename)
            .filter(Boolean))];
          machineList = lines.sort().map((linename) => ({
            name: linename,
            value: linename,
          }));
        } else {
          machineList = sortArray(machines, 'machinename').map(({ machinename }) => ({
            name: machinename,
            value: machinename,
          }));
        }
      }
      return machineList;
    },

    shiftList: ({ shifts }) => {
      let shiftList = [];
      if (shifts && shifts.length) {
        shiftList = shifts.map((s) => ({
          name: s,
          value: s,
        }));
      }
      return shiftList;
    },

    downtime: ({ downtimeList }, getters, rootState, rootGetters) => {
      let downtime = null;
      if (downtimeList && downtimeList.length) {
        downtime = rootGetters['webApp/filteredRecords'](downtimeList);
        downtime = rootGetters['webApp/sortedRecords'](downtime);
        downtime = rootGetters['webApp/groupedRecords'](downtime);
      }
      if (!downtime || !Object.keys(downtime).length) {
        downtime = null;
      }
      return downtime;
    },
  },
});
